// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import EditLink from 'Ui/components/common/data-table/edit-link';
import PartsCatalogLink from 'Ui/components/common/data-table/parts-catalog-link';
import {closeDialog, openDialog, openDialog as openReduxDialog} from 'Store/actions/dialogs';
import {fetchEffectData} from 'Utils/react-utils';
import {getFormattedCurrency} from 'Utils/unit-conversion-utils';
import {getPartRequestsForInventories} from 'Services/inventory-service';
import {getInventories, getMembershipHierarchyDealerships} from 'Services/membership-service';
import {ACTIONS, CATEGORY, LABELS} from 'Utils/analytics-utils';
import moment from 'moment';
import dialogTypes from 'Ui/components/common/dialog-types';
import {replaceTranslationNames, capitalizeFirstLetter} from 'Utils/translation-utils';
import {TOAST_TYPE} from 'Src/ui-core/constants/toast-constants';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {deletePartRequest} from 'Services/parts-service';
import {useNavBarActions} from 'Ui/react-hooks/use-navbar-actions';
import {PARTS_CATALOG_URL, SHOP_URL} from 'Ui/components/secondary-navbar/utils/dropdown-navbar-helper';
import {MANAGE_PARTS, MODIFY_FINANCIAL_DATA, VIEW_FINANCIAL_DATA} from 'Common/constants/business-activities';
import {isAuthorized} from 'Common/utils/authorization-handler';
import {isShopDeereUrlEnabled} from 'Src/ui-core/pages/onequip/parts/inventory';
import {IconNewWindow} from '@deere/icons';
import DataGridWrapper from 'Src/ui-core/components/common/data-table/data-grid-wrapper';
import {ColumnHelpers, Box} from '@deere/isg.component-library';
import {formatLocalizedTime} from 'Utils/time-utils';

async function fetchPartRequests({
    isMounted = () => true,
    membership,
    setAvailableDistributors,
    setLoading,
    setPartRequests
}) {
    setLoading(true);

    const {inventories} = await getInventories();
    const [
        partRequestsData,
        dealerships
    ] = await Promise.all([
        getPartRequestsForInventories(inventories, 'recommended'),
        getMembershipHierarchyDealerships(membership.membershipId)
    ]);

    if (isMounted()) {
        const formattedPartRequests = partRequestsData.map((partRequest) => ({
            ...partRequest,
            due: formatLocalizedTime(moment(partRequest.dateSelect).toDate(), {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            }),
            dueField: moment(partRequest.dateSelect, 'YYYYMMDD'),
            formattedTotalCost: getFormattedCurrency(partRequest.totalCost, {
                currencyPreference: membership.currencyPreference,
                maximumFractionDigits: 2
            })
        }));

        setAvailableDistributors(dealerships.dealerships);
        setLoading(false);
        setPartRequests(formattedPartRequests);
    }
}

function Recommended(props) {
    const {
        membership,
        openPurchaseOrderDialog,
        translations,
        openConfirmation,
        closeConfirmation,
        myJdPermissions
    } = props;

    const {addToast} = useToast();
    const [availableDistributors, setAvailableDistributors] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [partRequests, setPartRequests] = React.useState([]);

    React.useEffect(() => fetchEffectData(async (isMounted) => {
        await fetchPartRequests({
            isMounted,
            membership,
            setAvailableDistributors,
            setLoading,
            setPartRequests
        });
    }), [membership.membershipId]);

    const dialogOnClose = () => fetchPartRequests({
        membership,
        setAvailableDistributors,
        setLoading,
        setPartRequests
    });

    const handleDelete = async (partRequest) => {
        openConfirmation({
            message: replaceTranslationNames(translations.ONLINK_ITEM_THIS_CANNOT_BE_UNDONE, {
                '0': translations.ONLINK_PART_REQUEST.toLowerCase()
            }),
            title: replaceTranslationNames(translations.ONLINK_DELETE_ITEM, {
                '0': translations.ONLINK_PART_REQUEST
            }),
            async onContinue() {
                closeConfirmation();

                try {
                    await deletePartRequest(partRequest.partRequestId);

                    await fetchPartRequests({
                        membership,
                        setAvailableDistributors,
                        setLoading,
                        setPartRequests
                    });
                } catch {
                    addToast({
                        message: replaceTranslationNames(translations.ONLINK_CONFIRM_ITEM_DELETION_FAILED, {
                            '0': translations.ONLINK_PART_REQUEST.toLowerCase()
                        }),
                        type: TOAST_TYPE.ERROR
                    });
                }
            },
            onCancel: closeConfirmation
        });
    };

    const navBarActions = [
        {
            onClick: () => window.open(isShopDeereUrlEnabled(membership) ? SHOP_URL : PARTS_CATALOG_URL, '_blank'),
            title: 'SHOP_FOR_PARTS',
            Icon: <IconNewWindow/>,
            variant: 'primary'
        }
    ];

    useNavBarActions(navBarActions);

    function getDataGridColumns() {
        return [
            ColumnHelpers.localizedDateColumn({
                field: 'dueField',
                headerName: translations.DUE
            }, {
                width: 200
            }),
            {
                headerName: translations['Observation.QUANTITY'],
                field: 'quantity',
                width: 150,
                type: 'number'
            },
            {
                headerName: translations.NAME,
                width: 350,
                field: 'name'
            },
            {
                headerName: translations.MANUFACTURER,
                width: 300,
                field: 'manufacturerName'
            },
            {
                headerName: capitalizeFirstLetter(translations.PART_NUMBER),
                field: 'partNumber',
                width: 250,
                renderCell: (params) => {
                    return (
                        <PartsCatalogLink
                            analyticsEventData={{
                                action: ACTIONS.PARTS_INVENTORY,
                                category: CATEGORY.PARTS_CATALOG,
                                label: LABELS.PART_NUMBER,
                                value: params.row.partNumber
                            }}
                            manufacturerId={params.row.manufacturerId}
                            url={isShopDeereUrlEnabled(membership) ? `https://shop.deere.com/us/p/${params.row.partNumber}` :
                                `https://partscatalog.deere.com/jdrc/partdetails/partnum/${params.row.partNumber}`}
                            value={params.row.partNumber}
                        />
                    );
                }
            },
            {
                headerName: translations.ONLINK_ESTIMATED_COST,
                field: 'totalCost',
                type: 'number',
                myJdPermissions: VIEW_FINANCIAL_DATA,
                className: 'right-aligned',
                width: 200,
                headerClassName: 'right-aligned',
                renderCell: (params) => {
                    return params.row.formattedTotalCost;
                }
            },
            {
                headerName: '',
                field: 'actions',
                myJdPermissions: MODIFY_FINANCIAL_DATA,
                width: 250,
                sortable: false,
                renderCell: (params) => {
                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                marginRight: '8px',
                                justifyContent: 'end',
                                alignItems: 'center',
                                width: '100%',
                                height: '100%'
                            }}
                        >
                            <EditLink
                                onClick={() => {
                                    openPurchaseOrderDialog({
                                        availableDistributors,
                                        partRequest: params.row,
                                        translations,
                                        updatePartRequestData: dialogOnClose
                                    });
                                }}
                            >
                                {translations.ONLINK_ADD_TO_PURCHASE_ORDER}
                            </EditLink>
                        </Box>
                    );
                }
            }
        ].filter((option) => isAuthorized(
            {
                myJdPermissions: option.myJdPermissions
            }, {
                myJdPermissions,
                isMigrated: membership.isMigrated
            }
        ));
    }

    const canManageParts = isAuthorized(
        {
            myJdPermissions: MANAGE_PARTS
        }, {
            myJdPermissions,
            isMigrated: membership.isMigrated
        }
    );

    return (
        <DataGridWrapper
            columns={getDataGridColumns()}
            deleteHandler={handleDelete}
            getRowId={(row) => row.partRequestId}
            initialState={{
                sorting: {
                    sortModel: [{
                        field: 'dueField',
                        sort: 'asc'
                    }]
                }
            }}
            loading={loading}
            rows={partRequests}
            searchable={true}
            showDeleteColumn={canManageParts}
            slotProps={{
                root: {
                    disableColumnMenu: true
                }
            }}
            translations={translations}
        />
    );
}

Recommended.propTypes = {
    closeConfirmation: PropTypes.func,
    membership: PropTypes.membership,
    myJdPermissions: PropTypes.myJdPermissions,
    openConfirmation: PropTypes.func,
    openPurchaseOrderDialog: PropTypes.func,
    translations: PropTypes.translations
};

export function mapDispatchToProps(dispatch) {
    return {
        closeConfirmation() {
            dispatch(closeDialog(dialogTypes.CONFIRMATION_DIALOG));
        },
        openConfirmation(props) {
            dispatch(openDialog(dialogTypes.CONFIRMATION_DIALOG, props));
        },
        openPurchaseOrderDialog(props) {
            dispatch(openReduxDialog(dialogTypes.PURCHASE_ORDER_DIALOG, props));
        }
    };
}

export default connect(null, mapDispatchToProps)(Recommended);
